import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, onSnapshot, collection, query, where, documentId } from 'firebase/firestore';
import {
  PUBLIC_SITE_CONTENT_COLLECTION,
  LOGIN_PAGE_DOC_ID,
  resolveAppLayoutFooterHtml,
} from '../constants/loginPageContent';
import { LayoutDashboard, LogOut, Shield, PanelLeftOpen, PanelLeftClose, User, BarChart3, Map as MapIcon, ChevronDown, ChevronRight, Users, EyeOff } from 'lucide-react';
import { ROLES, isReadOnly, canManageAssociatedUsers, hasActiveAccess } from '../constants/roles';
import { UserRoleProvider } from '../context/UserRoleContext';
import { useDemoMode } from '../context/DemoModeContext';
import { resolveLifecycleState } from '../constants/buildStatus';
import TopBanner from './TopBanner';
import FontSizeAdjuster from './FontSizeAdjuster';
import DashTopNav from './DashTopNav';
import HelpDrawer from './HelpDrawer';
import FeedbackModal from './FeedbackModal';
import WelcomeTourModal from './tour/WelcomeTourModal';
import TourHubModal from './tour/TourHubModal';
import { HelpProvider, useHelp } from '../context/HelpContext';
import { ProductTourProvider, useProductTour } from '../context/ProductTourContext';
import { resolveParcelName } from '../utils/parcelName';

// The single, app-wide Help & Glossary drawer (handoff 12 §A). Rendered once at
// the top of Layout inside HelpProvider, so the "?" in DashTopNav and any
// page-local trigger open the SAME drawer. Reads the current page's context
// (registered via usePageHelp) so Analytics/Disclosure still get their bespoke
// "On this page" note + reference block, while every other page falls back to
// the HelpDrawer defaults. Kept OUTSIDE the isolated page-body stacking context
// (its own `fixed inset-0 z-50`) so it always overlays the nav and sidebar.
function SharedHelpDrawer() {
  const {
    open, closeHelp, locked, contextNote, referenceBlock,
    siteContext, focusTermId, clearFocusTerm,
  } = useHelp();
  const { openHub } = useProductTour();
  return (
    <HelpDrawer
      isOpen={open}
      onClose={closeHelp}
      lockOpen={locked}
      onTakeTour={openHub}
      contextNote={contextNote}
      referenceBlock={referenceBlock}
      siteContext={siteContext}
      focusTermId={focusTermId}
      clearFocusTerm={clearFocusTerm}
    />
  );
}

export default function Layout() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [farmIds, setFarmIds] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionOverride, setSubscriptionOverride] = useState(false);
  
  // Feedback & bug report modal (handoff 14). Mounted here as global chrome
  // (like the shared Help drawer), opened from the DashTopNav account menu.
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Sidebar State
  const [farms, setFarms] = useState([]);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [expandedFarm, setExpandedFarm] = useState(null);
  const [farmParcels, setFarmParcels] = useState({});
  const [appLayoutFooterHtml, setAppLayoutFooterHtml] = useState(() => resolveAppLayoutFooterHtml(null));
  const demoMode = useDemoMode();

  const roleRef = useRef(role);
  useEffect(() => {
    roleRef.current = role;
  }, [role]);

  const navigate = useNavigate();
  const location = useLocation();

  // Auto-collapse sidebar on map routes so the map gets full screen
  const isMapRoute = location.pathname.startsWith('/map/');
  useEffect(() => {
    if (isMapRoute) setNavCollapsed(true);
  }, [isMapRoute]);

  // Compact legal footer (editable in Admin / Superuser → Site content)
  useEffect(() => {
    const contentRef = doc(db, PUBLIC_SITE_CONTENT_COLLECTION, LOGIN_PAGE_DOC_ID);
    const unsub = onSnapshot(
      contentRef,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setAppLayoutFooterHtml(resolveAppLayoutFooterHtml(data));
      },
      (err) => {
        if (roleRef.current === ROLES.ADMIN) console.error('Layout public site content listener error:', err);
      }
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    setUser(currentUser);

    // Active per-chunk unsubscribers + the latest per-chunk result, keyed by
    // chunk index. Firestore's `where(documentId(), 'in', ids)` is capped at
    // 30 ids per query; any user with more assigned farms than that will
    // silently miss the rest unless we paginate. See
    // PHASE_NEXT_to_richard_integration_response_v13 §5.2 (Marcus reported
    // DHT missing from his sidebar even though it's assigned to him).
    let chunkUnsubs = [];
    const chunkBuckets = new Map();
    const teardownChunks = () => {
      for (const u of chunkUnsubs) { try { u(); } catch (_) {} }
      chunkUnsubs = [];
      chunkBuckets.clear();
    };

    // 1. LISTEN TO USER PROFILE
    const unsubUser = onSnapshot(doc(db, "users", currentUser.uid), (docSnapshot) => {
      if (!docSnapshot.exists()) return;
      teardownChunks();

      const data = docSnapshot.data();
      const userRole = data.role || ROLES.USER;
      setRole(userRole);
      setFarmIds(Array.isArray(data.farmIds) ? data.farmIds : []);
      setSubscriptionStatus(data.subscriptionStatus || null);
      setSubscriptionOverride(data.subscriptionOverride || false);

      // 2. FETCH FARMS BASED ON ROLE
      //    Phase 1: free / unassigned users still reach the sidebar; the
      //    public demo property is merged in below from DemoModeContext, so
      //    we don't bail out when assignedIds is empty.
      if (userRole === 'admin') {
        const q = query(collection(db, "farms"));
        const unsubAdmin = onSnapshot(
          q,
          (snapshot) => {
            const loadedFarms = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setFarms(loadedFarms);
          },
          (err) => {
            console.warn('[Layout] farms listener failed (admin):', err?.message || err);
            setFarms([]);
          }
        );
        chunkUnsubs.push(unsubAdmin);
        return;
      }

      const assignedIds = data.farmIds || [];
      if (assignedIds.length === 0) {
        setFarms([]);
        return;
      }

      // Chunk `in` queries at 30 (Firestore hard cap). For typical users
      // this is a single chunk and behaves identically to the previous
      // implementation. The per-chunk buckets are merged on each snapshot.
      const CHUNK = 30;
      const chunks = [];
      for (let i = 0; i < assignedIds.length; i += CHUNK) {
        chunks.push(assignedIds.slice(i, i + CHUNK));
      }
      const expectedCount = assignedIds.length;
      console.debug(`[Layout] ${expectedCount} farmIds assigned → ${chunks.length} chunk(s) of ≤${CHUNK}`);

      const recomputeFarms = () => {
        const merged = [];
        const seen = new Set();
        for (const bucket of chunkBuckets.values()) {
          for (const farm of bucket) {
            if (seen.has(farm.id)) continue;
            seen.add(farm.id);
            merged.push(farm);
          }
        }
        setFarms(merged);
        // Diagnostic: if Firestore returns fewer farms than we asked for,
        // some assigned ids point at farm docs that no longer exist (or
        // rules deny read). This is the most likely failure mode behind
        // "DHT is missing from my sidebar even though it's assigned".
        if (merged.length < expectedCount) {
          const returnedIds = new Set(merged.map(f => f.id));
          const missing = assignedIds.filter(id => !returnedIds.has(id));
          if (missing.length > 0) {
            console.warn(`[Layout] ${missing.length}/${expectedCount} assigned farms not returned by Firestore — missing ids:`, missing);
          }
        }
      };

      chunks.forEach((chunkIds, idx) => {
        const cq = query(collection(db, "farms"), where(documentId(), 'in', chunkIds));
        const unsubChunk = onSnapshot(
          cq,
          (snapshot) => {
            chunkBuckets.set(idx, snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            recomputeFarms();
          },
          (err) => {
            console.warn(`[Layout] farms listener failed (chunk ${idx + 1}/${chunks.length}, ${chunkIds.length} ids):`, err?.message || err);
            chunkBuckets.set(idx, []);
            recomputeFarms();
          }
        );
        chunkUnsubs.push(unsubChunk);
      });
    });

    return () => {
      unsubUser();
      teardownChunks();
    };
  }, []);

  // Phase 1: merge public-demo farms into the sidebar, deduplicated against
  // anything already in the user's own list. Demo properties appear first so
  // they're the natural entry point for free users.
  const sidebarFarms = useMemo(() => {
    const ownIds = new Set(farms.map((f) => f.id));
    const demoExtras = (demoMode.publicDemoFarms || []).filter((f) => !ownIds.has(f.id));
    return [...demoExtras, ...farms].filter((f) => demoMode.isFarmInDemoScope(f.id));
  }, [farms, demoMode]);

  useEffect(() => {
    if (!expandedFarm) {
      setFarmParcels({});
      return;
    }
    setFarmParcels({ [expandedFarm]: [] });
    const unsub = onSnapshot(collection(db, 'farms', expandedFarm, 'parcels'), (snapshot) => {
      const all = [];
      snapshot.forEach(d => {
        all.push({ docId: d.id, data: d.data() || {} });
      });
      const hasConfigParcel = all.some(({ data }) => data.source === 'config');
      const parcels = [];
      for (const { docId, data } of all) {
        if (data.analyticsParcelName === 'WHOLE_SITE') continue;
        if (hasConfigParcel && docId === '0') continue;
        if (hasConfigParcel && /^\d+$/.test(docId) && docId.length <= 3 && !data.source) continue;
        // `resolveParcelName` is the canonical reader — see
        // frontend/src/utils/parcelName.js. It correctly handles the
        // customName > displayName > legacy field fallback chain and
        // discards id-equals-name cases (e.g. WLD where parcel name was
        // historically just the parcel_id).
        const displayLabel = resolveParcelName({ ...data, id: docId }) || docId;
        parcels.push({
          id: docId,
          displayName: displayLabel,
          displayLabel,
          analyticsName: data.analyticsParcelName || data.parcel_id || data.id || docId
        });
      }
      parcels.sort((a, b) => (a.displayLabel || a.displayName).localeCompare(b.displayLabel || b.displayName));
      setFarmParcels({ [expandedFarm]: parcels });
    });
    return () => unsub();
  }, [expandedFarm]);

  const handleLogout = async () => { await signOut(auth); navigate('/login'); };
  
  const navClass = (path) => location.pathname.startsWith(path) 
    ? "w-full flex items-center text-sm font-semibold bg-eddp-teal/20 text-white px-3 py-2.5 rounded-lg border border-eddp-teal/50" 
    : "w-full flex items-center text-sm font-medium text-eddp-cream/70 hover:bg-white/10 hover:text-white px-3 py-2.5 rounded-lg border border-transparent";

  return (
    <HelpProvider>
    <ProductTourProvider farmIds={sidebarFarms.map((f) => f.id)}>
    <div className="flex h-screen bg-eddp-primary font-sans text-slate-900">
      {/* SIDEBAR — collapsible (deep-green brand shell, handoffs/00-foundation.md A3) */}
      <aside className={`bg-eddp-primary border-r border-white/10 flex flex-col flex-shrink-0 z-20 transition-all duration-200 ${navCollapsed ? 'w-14' : 'w-72'}`}>
        {/* Header */}
        <div className="h-32 flex items-center border-b border-white/10 px-3">
          {navCollapsed ? (
            <button onClick={() => setNavCollapsed(false)} className="mx-auto p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="Expand navigation">
              <PanelLeftOpen size={20} className="text-eddp-cream/80" />
            </button>
          ) : (
            <div className="flex items-center justify-between w-full px-2">
              {/* Brand lockup (handoffs/01-design-pass.md §1): the colourful gradient
                  mark (same asset as the favicon) + live cream wordmark + strap on the
                  deep-green sidebar. This mark SVG is transparent with no internal
                  padding, so it keeps a small gap to the wordmark and uses w-auto to
                  preserve its aspect. Live text renders crisply in Manrope at any scale. */}
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                <img
                  src="/ei-mark.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-10 w-auto shrink-0"
                />
                <div className="min-w-0 leading-[1.12]">
                  <div className="font-sans font-extrabold text-eddp-cream text-[1rem]">
                    Ecological Intelligence
                  </div>
                  <div className="font-sans text-eddp-cream/70 text-[0.7rem] mt-0.5">
                    understand your land
                  </div>
                </div>
              </div>
              <button onClick={() => setNavCollapsed(true)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors shrink-0" title="Collapse navigation">
                <PanelLeftClose size={18} className="text-eddp-cream/60" />
              </button>
            </div>
          )}
        </div>

        {/* Expanded content */}
        {!navCollapsed && (
          <>
            {isReadOnly(role) && (
              <div className="px-4 py-2 border-b border-white/10">
                <div className="flex items-center gap-1.5 text-[11px] text-amber-200 bg-amber-400/15 px-2.5 py-1.5 rounded-lg font-medium">
                  <EyeOff size={12} /> Read-only access
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-3 py-2">
              <div className="text-xs font-semibold text-eddp-cream/50 uppercase tracking-wider mb-2 mt-2 px-2">Your Properties</div>
              
              {sidebarFarms.length === 0 && (
                <div className="text-xs text-eddp-cream/50 italic px-4 py-2">
                  {demoMode.isDemoModeEnabled ? 'No properties in demo view.' : 'No properties assigned.'}
                </div>
              )}

              {sidebarFarms.map((farm, farmIndex) => {
                const isExpanded = expandedFarm === farm.id;
                const parcels = farmParcels[farm.id] || [];
                const farmName = demoMode.getFarmDisplayName(farm, farmIndex);
                const isDemoFarm = !!farm.isPublicDemo;
                return (
                  <div key={farm.id} className="mb-1">
                    <div className="p-2.5 rounded-lg hover:bg-white/5 border border-transparent hover:border-white/10 transition-all group">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setExpandedFarm(isExpanded ? null : farm.id); }}
                          className="p-0.5 text-eddp-cream/60 hover:text-white flex-shrink-0"
                        >
                          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        {/* Handoff 20 §C: property entry opens Overview; Map /
                            Analytics / Reports live on the dashboard card, so
                            the per-row icon cluster is dropped. */}
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/overview/${farm.id}`)}>
                          <div className="font-bold text-sm text-white truncate flex items-center gap-1.5">
                            <span className="truncate">{farmName}</span>
                            {isDemoFarm && (
                              <span className="shrink-0 text-[9px] uppercase font-bold px-1.5 py-[1px] rounded-full bg-eddp-teal/25 text-white" title="Demo property">
                                Demo
                              </span>
                            )}
                          </div>
                          {/* Sidebar status dot prefers v0.3 `farm.buildStatus`; falls
                              back to legacy `farm.status` so the 5 pre-v0.3 farms
                              render unchanged. See constants/buildStatus.js. */}
                          {(() => {
                            const lc = resolveLifecycleState(farm);
                            return (
                              <div className="text-xs text-eddp-cream/60 flex items-center gap-1 mt-0.5">
                                <span className={`w-2 h-2 rounded-full ${lc.dotClass}`}></span>
                                {lc.label}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="ml-5 border-l border-white/10 pl-2 py-1">
                        {parcels.length === 0 ? (
                          <div className="text-[11px] text-eddp-cream/50 italic px-2 py-1.5">No parcels synced yet</div>
                        ) : (
                          parcels.map((p, parcelIndex) => {
                            const label = p.displayLabel ?? p.displayName;
                            const demoLabel = demoMode.getParcelDisplayName(farm.id, p.analyticsName || p.id, label, parcelIndex);
                            return (
                            <div key={p.id} className="flex items-center gap-1.5 py-1 px-1.5 rounded hover:bg-white/5">
                              <span className="flex-1 text-xs text-eddp-cream/70 truncate">{demoLabel}</span>
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => navigate(`/map/${farm.id}?parcel=${encodeURIComponent(p.analyticsName)}`)} className="p-0.5 rounded hover:bg-white/10 text-eddp-cream/60 hover:text-white" title={`${demoLabel} map`}>
                                  <MapIcon size={14} />
                                </button>
                                <button onClick={() => navigate(`/analytics/${farm.id}/field/${encodeURIComponent(p.analyticsName)}`)} className="p-0.5 rounded hover:bg-white/10 text-eddp-cream/60 hover:text-white" title={`${demoLabel} analytics`}>
                                  <BarChart3 size={14} />
                                </button>
                              </div>
                            </div>
                          ); })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-white/10 bg-eddp-green2">
              {/* UI text-size control (handoffs/01-design-pass.md §4) */}
              <FontSizeAdjuster />
              <button onClick={() => navigate('/dashboard')} className={navClass('/dashboard')}><LayoutDashboard size={18} className="mr-3"/> Dashboard</button>
              <button onClick={() => navigate('/profile')} className={navClass('/profile')}><User size={18} className="mr-3"/> Profile</button>
              {canManageAssociatedUsers(role) && role !== ROLES.ADMIN && (
                <button onClick={() => navigate('/superuser')} className={navClass('/superuser')}><Users size={18} className="mr-3"/> Manage Users</button>
              )}
              {role === ROLES.ADMIN && (
                <button onClick={() => navigate('/admin')} className={navClass('/admin')}><Shield size={18} className="mr-3"/> Admin Console</button>
              )}
              {/* The account role/tier is shown in the top-right account chip
                  (DashTopNav), so the previously-orphaned sidebar role pill is
                  dropped here to reclaim the row and avoid duplicating it. */}
              <button onClick={handleLogout} className="w-full flex items-center text-xs text-eddp-cream/60 mt-2 px-2 py-2 hover:text-red-300"><LogOut size={14} className="mr-2"/> Sign Out</button>
            </div>
          </>
        )}

        {/* Collapsed mini-nav */}
        {navCollapsed && (
          <div className="flex-1 flex flex-col items-center py-3 gap-2">
            {sidebarFarms.slice(0, 6).map((farm, farmIndex) => (
              <button key={farm.id} onClick={() => navigate(`/overview/${farm.id}`)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold bg-white/10 hover:bg-white/20 hover:text-white text-eddp-cream transition-colors border border-transparent hover:border-eddp-teal/40"
                title={demoMode.getFarmDisplayName(farm, farmIndex)}>
                {demoMode.getFarmDisplayName(farm, farmIndex).charAt(0).toUpperCase()}
              </button>
            ))}
          </div>
        )}
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 relative overflow-hidden flex flex-col min-h-0">
        {/* Sticky top-of-page banner — sits above the scroll area so it
            stays visible regardless of in-page scroll. TopBanner orchestrates
            announcements (admin → Announcements) over the legacy promo
            banner (admin → Promo banner). When an admin-published
            announcement matches the signed-in user it overrides the promo
            banner in the same on-page position. */}
        <TopBanner />
        {/* Horizontal dash-topnav (handoffs/01-design-pass.md §6): all tabs for
            all roles + the role chip. Sits below the announcement noticebar,
            above the page content, additive to the sidebar. */}
        <UserRoleProvider role={role} uid={user?.uid} farmIds={farmIds}>
        <DashTopNav role={role} user={user} onOpenFeedback={() => setFeedbackOpen(true)} />
        {/* `isolate` on map routes contains the map's high z-index floating
            panels (up to z-[2000]) inside this page-body stacking context, so
            the DashTopNav account/help dropdowns (lifted above via the nav's
            own z-index) stay clickable over the map (handoff 12 §B). Non-map
            pages keep the plain stacking context. */}
        <div className={`flex-1 overflow-auto bg-eddp-light relative min-h-0${isMapRoute ? ' isolate' : ''}`}>
            <Outlet />
        </div>
        </UserRoleProvider>
        <footer
          className="app-layout-footer"
          role="contentinfo"
          dangerouslySetInnerHTML={{ __html: appLayoutFooterHtml }}
        />
      </main>

    </div>
    <SharedHelpDrawer />
    <FeedbackModal
      open={feedbackOpen}
      onClose={() => setFeedbackOpen(false)}
      page={(location.pathname.split('/')[1] || 'dashboard')}
    />
    <WelcomeTourModal />
    <TourHubModal />
    </ProductTourProvider>
    </HelpProvider>
  );
}
